import React, { useState } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  CheckCircle2, 
  FileText,
  Play,
  ChevronLeft, 
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Custom components for the lecture
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r">
      <div className="flex items-start">
        <CheckCircle2 className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900">{children}</div>
      </div>
    </div>
  );
};

const ScientificDiagram = ({ 
  title, 
  caption, 
  children 
}: { 
  title: string; 
  caption: string; 
  children: React.ReactNode;
}) => {
  return (
    <figure className="my-8 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{caption}</p>
      </div>
      <div className="overflow-hidden">
        {children}
      </div>
    </figure>
  );
};

const VideoPlaceholder = () => {
  return (
    <div className="w-full aspect-video bg-gray-100 rounded-lg flex items-center justify-center mb-8">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Play className="h-8 w-8 text-white" />
        </div>
        <p className="text-gray-600 font-medium">Lecture video will be available after the live session</p>
      </div>
    </div>
  );
};

// Slideshow component
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };
  
  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };
  
  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-8">
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Lecture Slides</h3>
        <div className="text-sm text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
      </div>
      
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{slides[currentSlide].title}</h2>
        <div className="mb-6 min-h-[300px]">
          {slides[currentSlide].content}
        </div>
      </div>
      
      <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
        <button 
          onClick={goToPrevSlide}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Previous
        </button>
        
        <div className="flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2.5 w-2.5 rounded-full ${
                index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <button 
          onClick={goToNextSlide}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          Next
          <ChevronRight className="h-5 w-5 ml-1" />
        </button>
      </div>
    </div>
  );
};

  const EstrogenMolecularFoundations: React.FC = () => {

  const resources = [
    {
      title: 'Lecture Notes',
      link: '#',
      icon: <FileText size={16} />
    },
    {
      title: 'References',
      link: '#',
      icon: <FileText size={16} />
    }
  ];

  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Introduction to Estrogen Metabolism",
      content: (
        <>
          <p className="mb-4">Estrogens are steroid hormones derived from cholesterol that play crucial roles in reproductive and systemic physiology. This lecture explores estrogen's molecular mechanisms, pathways, and clinical implications from a systems biology perspective.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Sources in Premenopausal Women</h3>
              <p>The ovaries produce the majority of estrogen (primarily 17β-estradiol, E2), with minor contributions from adrenal precursors. Corpus luteum and theca cells are key producers in the ovarian cycle.</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Sources in Postmenopausal Women and Men</h3>
              <p>Extragonadal tissues (adipose, bone, brain) become the predominant sites of estrogen synthesis via aromatization of circulating androgens. Peripheral conversion is essential for maintaining estrogen functions.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Understanding estrogen metabolism is critical for addressing hormone-related conditions across the lifespan. The shift from ovarian to extragonadal estrogen production after menopause represents a fundamental physiological transition that impacts multiple body systems and creates unique therapeutic opportunities.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 2,
      title: "Estrogen Synthesis and Degradation Pathways",
      content: (
        <>
          <ScientificDiagram 
            title="Estrogen Metabolism Pathways" 
            caption="Key enzymatic pathways in estrogen synthesis and degradation"
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Estrogen Synthesis and Metabolism</text>
                
                {/* Synthesis Pathway Section */}
                <g transform="translate(400, 120)">
                  <rect x="-330" y="-30" width="660" height="120" rx="5" fill="#f9f9f9" stroke="#999" strokeWidth="1" />
                  <text x="0" y="-10" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Estrogen Synthesis</text>
                  
                  {/* Cholesterol */}
                  <g transform="translate(-250, 40)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#FFE4C4" stroke="#d4a76a" strokeWidth="1.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">Cholesterol</text>
                  </g>
                  
                  {/* Arrow 1 */}
                  <path d="M-190, 40 L-140, 40" stroke="#888" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                  <text x="-165" y="30" textAnchor="middle" fontSize="12" fill="#666">CYP11A1</text>
                  
                  {/* Pregnenolone */}
                  <g transform="translate(-90, 40)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#FFECB3" stroke="#d4a76a" strokeWidth="1.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">Pregnenolone</text>
                  </g>
                  
                  {/* Arrow 2 */}
                  <path d="M-30, 40 L20, 40" stroke="#888" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                  <text x="-5" y="30" textAnchor="middle" fontSize="12" fill="#666">CYP17A1</text>
                  
                  {/* DHEA */}
                  <g transform="translate(70, 40)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D1E9E9" stroke="#669999" strokeWidth="1.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">DHEA</text>
                  </g>
                  
                  {/* Arrow 3 */}
                  <path d="M130, 40 L180, 40" stroke="#888" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                  <text x="155" y="30" textAnchor="middle" fontSize="12" fill="#666">3β-HSD</text>
                  
                  {/* Androstenedione */}
                  <g transform="translate(230, 40)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D1E9E9" stroke="#669999" strokeWidth="1.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">Androstenedione</text>
                  </g>
                  
                  {/* Enzyme markers */}
                  <defs>
                    <marker id="arrowhead" markerWidth="5" markerHeight="5" refX="5" refY="2.5" orient="auto">
                      <polygon points="0 0, 5 2.5, 0 5" fill="#888" />
                    </marker>
                  </defs>
                </g>
                
                {/* Central Estrogens Section */}
                <g transform="translate(400, 290)">
                  <rect x="-330" y="-30" width="660" height="120" rx="5" fill="#FFF0F5" stroke="#d66a9c" strokeWidth="1.5" />
                  <text x="0" y="-10" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#d66a9c">Primary Estrogens</text>
                  
                  {/* Estrone */}
                  <g transform="translate(-200, 40)">
                    <rect x="-70" y="-20" width="140" height="40" rx="5" fill="#FFD6E7" stroke="#d66a9c" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#111">Estrone (E1)</text>
                  </g>
                  
                  {/* Arrows between estrogens */}
                  <path d="M-120, 40 L-20, 40" stroke="#d66a9c" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                  <text x="-70" y="30" textAnchor="middle" fontSize="12" fill="#d66a9c">17β-HSD</text>
                  <path d="M120, 30 L20, 30" stroke="#d66a9c" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                  <text x="70" y="20" textAnchor="middle" fontSize="12" fill="#d66a9c">17β-HSD</text>
                  
                  {/* Estradiol */}
                  <g transform="translate(0, 40)">
                    <rect x="-80" y="-20" width="160" height="40" rx="5" fill="#FFB6C1" stroke="#d66a9c" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111">17β-Estradiol (E2)</text>
                  </g>
                  
                  {/* Estriol */}
                  <g transform="translate(200, 40)">
                    <rect x="-70" y="-20" width="140" height="40" rx="5" fill="#FFD6E7" stroke="#d66a9c" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#111">Estriol (E3)</text>
                  </g>
                </g>
                
                {/* Metabolism Section */}
                <g transform="translate(400, 450)">
                  <rect x="-330" y="-20" width="660" height="90" rx="5" fill="#f9f9f9" stroke="#999" strokeWidth="1" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Metabolism Pathways</text>
                  
                  {/* Metabolic pathway blocks */}
                  <g transform="translate(-200, 35)">
                    <rect x="-90" y="-15" width="180" height="30" rx="5" fill="#E8F8F5" stroke="#20B2AA" strokeWidth="1.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">2-Hydroxy pathway</text>
                  </g>
                  
                  <g transform="translate(0, 35)">
                    <rect x="-90" y="-15" width="180" height="30" rx="5" fill="#F2D7D5" stroke="#CD5C5C" strokeWidth="1.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">4-Hydroxy pathway</text>
                  </g>
                  
                  <g transform="translate(200, 35)">
                    <rect x="-90" y="-15" width="180" height="30" rx="5" fill="#E5E8E8" stroke="#566573" strokeWidth="1.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">16α-Hydroxy pathway</text>
                  </g>
                </g>
                
                {/* Arrows connecting main sections */}
                <path d="M230, 170 L230, 260" stroke="#888" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <text x="220" y="210" textAnchor="end" fontSize="14" fill="#d66a9c">CYP19A1</text>
                <text x="220" y="230" textAnchor="end" fontSize="14" fill="#d66a9c">(Aromatase)</text>
                
                <path d="M570, 170 L570, 260" stroke="#888" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <text x="580" y="210" textAnchor="start" fontSize="14" fill="#d66a9c">CYP19A1</text>
                <text x="580" y="230" textAnchor="start" fontSize="14" fill="#d66a9c">(Aromatase)</text>
                
                <path d="M400, 350 L400, 390" stroke="#888" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <text x="410" y="370" textAnchor="start" fontSize="14" fill="#666">CYP1A1/1B1</text>
              </svg>
            </div>
          </ScientificDiagram>

          <p className="mb-4">Estrogen metabolism involves a complex series of enzymatic conversions with significant biological and clinical implications:</p>
          
          <ul className="list-disc list-inside mb-4 space-y-2">
            <li><span className="font-medium">Precursor Conversion:</span> Androstenedione (adrenal/ovarian steroid) is converted by aromatase (CYP19A1) to estrone (E1)</li>
            <li><span className="font-medium">Interconversion:</span> Estrone can be further reduced to estradiol by 17β-hydroxysteroid dehydrogenase, or estradiol can be oxidized back to estrone</li>
            <li><span className="font-medium">Phase I Metabolism:</span> Estrogen undergoes hydroxylation by cytochrome P450 enzymes (CYP1A1, CYP1B1) at positions 2, 4, or 16α</li>
            <li><span className="font-medium">Phase II Metabolism:</span> Catechol-O-methyltransferase (COMT) methylates catechol estrogens (2-OH and 4-OH) to methoxy derivatives for excretion</li>
          </ul>
          
          <KeyTakeaway>
            Genetic polymorphisms in estrogen metabolism pathways (e.g., COMT Val158Met or CYP1B1 Leu432Val) can alter estrogen metabolite profiles, affecting hormone sensitivity and potentially influencing cancer risk. For example, a high 2-hydroxyestrone/16α-hydroxyestrone ratio (favoring the "2-OH pathway") has been associated with lower breast cancer risk.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 3,
      title: "Estrogen Receptor Subtypes and Signaling",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">ERα (ESR1)</h3>
              <p className="mb-2">Primary distribution:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Reproductive tissues (uterus, ovary)</li>
                <li>Breast tissue and breast cancer cells</li>
                <li>Hypothalamus and specific brain regions</li>
                <li>Liver</li>
              </ul>
              <p className="mt-2">Associated with proliferative and reproductive functions, classically mediates estrogen's growth-promoting effects.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-md">
              <h3 className="font-medium text-green-900 mb-2">ERβ (ESR2)</h3>
              <p className="mb-2">Primary distribution:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Ovarian granulosa cells</li>
                <li>Vascular endothelium</li>
                <li>Lung</li>
                <li>CNS and glial cells</li>
                <li>Prostate and colon epithelia</li>
              </ul>
              <p className="mt-2">Often associated with anti-proliferative, differentiating, and protective effects, acting as a biological "brake" on ERα activity.</p>
            </div>
          </div>
          
          <ScientificDiagram 
            title="Estrogen Signaling Mechanisms" 
            caption="Genomic and non-genomic pathways of estrogen receptor signaling"
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Estrogen Receptor Signaling Pathways</text>
                
                {/* Cell membrane */}
                <path d="M100,100 L700,100 L700,400 L100,400 Z" fill="#f9f9f9" stroke="#888" strokeWidth="2" />
                <path d="M100,100 L700,100" stroke="#555" strokeWidth="4" strokeDasharray="6,3" />
                <text x="120" y="120" fontSize="14" fill="#555">Cell membrane</text>
                
                {/* Cell nucleus */}
                <ellipse cx="400" cy="280" rx="160" ry="90" fill="#EFF8FB" stroke="#888" strokeWidth="2" />
                <text x="400" y="225" textAnchor="middle" fontSize="14" fill="#555">Nucleus</text>
                
                {/* Estrogen and Receptors */}
                <circle cx="200" cy="90" r="15" fill="#FFB6C1" stroke="#d66a9c" strokeWidth="2" />
                <text x="200" y="95" textAnchor="middle" fontSize="12" fill="#111">E2</text>
                
                <circle cx="250" cy="90" r="15" fill="#FFB6C1" stroke="#d66a9c" strokeWidth="2" />
                <text x="250" y="95" textAnchor="middle" fontSize="12" fill="#111">E2</text>
                
                <circle cx="550" cy="90" r="15" fill="#FFB6C1" stroke="#d66a9c" strokeWidth="2" />
                <text x="550" y="95" textAnchor="middle" fontSize="12" fill="#111">E2</text>
                
                {/* Membrane receptor */}
                <rect x="530" y="100" width="40" height="20" rx="5" fill="#98FB98" stroke="#2E8B57" strokeWidth="2" />
                <text x="550" y="115" textAnchor="middle" fontSize="12" fill="#111">GPR30</text>
                
                {/* Nuclear receptors */}
                <rect x="180" y="150" width="40" height="20" rx="5" fill="#ADD8E6" stroke="#4682B4" strokeWidth="2" />
                <text x="200" y="165" textAnchor="middle" fontSize="12" fill="#111">ERα</text>
                
                <rect x="230" y="150" width="40" height="20" rx="5" fill="#90EE90" stroke="#3CB371" strokeWidth="2" />
                <text x="250" y="165" textAnchor="middle" fontSize="12" fill="#111">ERβ</text>
                
                {/* Genomic Pathway */}
                <path d="M200,100 L200,145" stroke="#d66a9c" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                <path d="M250,100 L250,145" stroke="#d66a9c" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                <path d="M200,175 L290,225" stroke="#4682B4" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                <path d="M250,175 L300,225" stroke="#3CB371" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                
                {/* ER Dimerization and DNA Binding */}
                <rect x="290" y="230" width="60" height="25" rx="5" fill="#E6E6FA" stroke="#8A2BE2" strokeWidth="1.5" />
                <text x="320" y="247" textAnchor="middle" fontSize="12" fill="#111">ER Dimer</text>
                
                <rect x="420" y="265" width="90" height="25" rx="5" fill="#FFDAB9" stroke="#CD853F" strokeWidth="1.5" />
                <text x="465" y="282" textAnchor="middle" fontSize="12" fill="#111">ERE on DNA</text>
                
                <path d="M355,242 L415,265" stroke="#8A2BE2" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                
                <rect x="400" y="320" width="120" height="25" rx="5" fill="#FFFACD" stroke="#DAA520" strokeWidth="1.5" />
                <text x="460" y="337" textAnchor="middle" fontSize="12" fill="#111">Gene Transcription</text>
                
                <path d="M465,295 L465,315" stroke="#CD853F" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                
                {/* Non-genomic Pathway */}
                <path d="M550,125 L550,160" stroke="#2E8B57" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                
                <rect x="520" y="165" width="60" height="25" rx="5" fill="#FFDEAD" stroke="#CD853F" strokeWidth="1.5" />
                <text x="550" y="182" textAnchor="middle" fontSize="12" fill="#111">PI3K/Akt</text>
                
                <rect x="610" y="220" width="60" height="25" rx="5" fill="#FFDEAD" stroke="#CD853F" strokeWidth="1.5" />
                <text x="640" y="237" textAnchor="middle" fontSize="12" fill="#111">MAPK</text>
                
                <path d="M550,195 L610,220" stroke="#CD853F" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                <path d="M640,250 L580,320" stroke="#CD853F" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                
                <rect x="520" y="320" width="120" height="25" rx="5" fill="#FFFACD" stroke="#DAA520" strokeWidth="1.5" />
                <text x="580" y="337" textAnchor="middle" fontSize="12" fill="#111">Rapid Cellular Effects</text>
                
                {/* Labels */}
                <text x="150" y="195" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#4682B4">Genomic</text>
                <text x="150" y="210" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#4682B4">Pathway</text>
                
                <text x="650" y="195" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2E8B57">Non-genomic</text>
                <text x="650" y="210" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2E8B57">Pathway</text>
                
                {/* Definitions */}
                <defs>
                  <marker id="arrowhead" markerWidth="5" markerHeight="5" refX="5" refY="2.5" orient="auto">
                    <polygon points="0 0, 5 2.5, 0 5" fill="#888" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p className="mb-4">Estrogen signaling operates through multiple mechanisms:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="border border-gray-200 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Genomic (Nuclear) Pathway</h3>
              <p className="text-sm">Estrogen diffuses into the cell and binds ERα or ERβ, which then dimerize and translocate to the nucleus. The activated receptor complex binds to Estrogen Response Elements (EREs) in DNA and recruits coactivators/corepressors to regulate gene transcription. This classical pathway takes hours to produce effects but creates sustained changes in cell function.</p>
            </div>
            <div className="border border-gray-200 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Non-genomic (Membrane) Pathway</h3>
              <p className="text-sm">Rapid signaling occurs when estrogen binds membrane-associated estrogen receptors (mERs) and G protein-coupled estrogen receptor (GPR30/GPER). This activates second messengers like calcium flux, cAMP, and kinase cascades (PI3K/Akt, MAPK) within minutes, mediating rapid physiological responses like vasodilation, synaptic plasticity, and glucose uptake.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The balance between ERα and ERβ signaling is critical in determining estrogen's tissue-specific effects. Understanding the ER subtype distribution explains why selective estrogen receptor modulators (SERMs) like tamoxifen can have opposing effects in different tissues (antagonist in breast, agonist in bone). Modern hormone therapy approaches increasingly focus on targeting specific receptor subtypes to maximize beneficial effects while minimizing risks.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "Interplay Between Estrogen and Testosterone",
      content: (
        <>
          <ScientificDiagram 
            title="Estrogen-Testosterone Interplay" 
            caption="Hormonal dynamics before and after menopause"
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title & Labels */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Estrogen-Testosterone Dynamics Across Menopause</text>
                
                <text x="250" y="100" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#d66a9c">Premenopausal State</text>
                <text x="550" y="100" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#666">Postmenopausal State</text>
                
                {/* Dividing line */}
                <line x1="400" y1="80" x2="400" y2="420" stroke="#888" strokeWidth="2" strokeDasharray="8,4" />
                
                {/* Premenopausal diagram */}
                <g transform="translate(250, 260)">
                  {/* Ovary */}
                  <ellipse cx="0" cy="0" rx="80" ry="60" fill="#FFE4E1" stroke="#d66a9c" strokeWidth="2" />
                  <text x="0" y="5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Ovary</text>
                  
                  {/* Ovarian hormones */}
                  <g transform="translate(-40, -25)">
                    <circle cx="0" cy="0" r="18" fill="#FFB6C1" stroke="#d66a9c" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">E2</text>
                    <text x="0" y="-25" textAnchor="middle" fontSize="12" fill="#d66a9c">Primary</text>
                  </g>
                  
                  <g transform="translate(40, -25)">
                    <circle cx="0" cy="0" r="15" fill="#ADD8E6" stroke="#4682B4" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">T</text>
                  </g>
                  
                  {/* Adrenal gland */}
                  <g transform="translate(-120, -100)">
                    <path d="M-30,0 C-30,-20 30,-20 30,0 C30,20 -30,20 -30,0 Z" fill="#FFE4B5" stroke="#CD853F" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#333">Adrenal</text>
                  </g>
                  
                  {/* DHEA/Androstenedione */}
                  <g transform="translate(-70, -70)">
                    <circle cx="0" cy="0" r="15" fill="#D1E9E9" stroke="#669999" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="10" fill="#111">DHEA</text>
                  </g>
                </g>
                
                {/* Postmenopausal diagram */}
                <g transform="translate(550, 260)">
                  {/* Post-menopausal ovary */}
                  <ellipse cx="0" cy="0" rx="60" ry="45" fill="#F5F5F5" stroke="#888" strokeWidth="2" />
                  <text x="0" y="5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Ovary</text>
                  <text x="0" y="25" textAnchor="middle" fontSize="12" fill="#888">(Reduced function)</text>
                  
                  {/* Adrenal gland */}
                  <g transform="translate(-120, -100)">
                    <path d="M-30,0 C-30,-20 30,-20 30,0 C30,20 -30,20 -30,0 Z" fill="#FFE4B5" stroke="#CD853F" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#333">Adrenal</text>
                  </g>
                  
                  {/* DHEA/Androstenedione */}
                  <g transform="translate(-70, -70)">
                    <circle cx="0" cy="0" r="15" fill="#D1E9E9" stroke="#669999" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="10" fill="#111">DHEA</text>
                  </g>
                  
                  {/* Adipose tissue */}
                  <g transform="translate(80, -80)">
                    <path d="M-40,0 C-40,-25 40,-25 40,0 C40,25 -40,25 -40,0 Z" fill="#FFFACD" stroke="#DAA520" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#333">Adipose</text>
                  </g>
                  
                  {/* Peripheral conversion */}
                  <g transform="translate(30, -40)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fill="#888">Peripheral</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="12" fill="#888">Conversion</text>
                    <path d="M0,20 L0,40" stroke="#888" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                  </g>
                  
                  {/* Resulting hormones */}
                  <g transform="translate(40, -25)">
                    <circle cx="0" cy="0" r="15" fill="#ADD8E6" stroke="#4682B4" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">T</text>
                  </g>
                  
                  <g transform="translate(80, 0)">
                    <circle cx="0" cy="0" r="15" fill="#FFD6E7" stroke="#d66a9c" strokeWidth="2" />
                    <text x="0" y="5" textAnchor="middle" fontSize="14" fill="#111">E1</text>
                    <text x="0" y="-25" textAnchor="middle" fontSize="12" fill="#d66a9c">Primary</text>
                  </g>
                  
                  <path d="M50,-15 L70,-10" stroke="#4682B4" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
                  <text x="70" y="-20" textAnchor="middle" fontSize="12" fill="#4682B4">Aromatase</text>
                </g>
                
                {/* Effects and key points */}
                <g transform="translate(250, 380)">
                  <rect x="-150" y="0" width="300" height="30" rx="5" fill="#FFF0F5" stroke="#d66a9c" strokeWidth="1.5" />
                  <text x="0" y="20" textAnchor="middle" fontSize="14" fill="#333">Estradiol (E2) dominant phase</text>
                </g>
                
                <g transform="translate(550, 380)">
                  <rect x="-150" y="0" width="300" height="30" rx="5" fill="#F0F8FF" stroke="#4682B4" strokeWidth="1.5" />
                  <text x="0" y="20" textAnchor="middle" fontSize="14" fill="#333">Relative androgen dominance</text>
                </g>
                
                {/* Definitions */}
                <defs>
                  <marker id="arrowhead" markerWidth="5" markerHeight="5" refX="5" refY="2.5" orient="auto">
                    <polygon points="0 0, 5 2.5, 0 5" fill="#888" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p className="mb-4">Estrogen and testosterone have a dynamic interplay that changes significantly during menopause:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-medium text-rose-900 mb-2">Premenopausal Dynamics</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Ovaries produce estradiol (E2) as the dominant estrogen</li>
                <li>Ovaries and adrenals contribute to testosterone production</li>
                <li>Estradiol:testosterone ratio is typically higher</li>
                <li>Cyclical hormonal patterns follow the menstrual cycle</li>
                <li>Negative feedback regulation via hypothalamic-pituitary axis</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-blue-900 mb-2">Postmenopausal Dynamics</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Ovarian estradiol production ceases</li>
                <li>Androgen precursors from adrenals and residual ovarian function continue</li>
                <li>Peripheral aromatization (especially in adipose tissue) becomes the primary source of estrogen</li>
                <li>Estrone (E1) becomes the dominant estrogen form</li>
                <li>Relative androgen:estrogen ratio increases</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Understanding the interplay between estrogen and testosterone is critical for effective hormone therapy. After menopause, an overweight woman may have higher circulating estrone due to increased adipose aromatization, while a lean woman typically has very low estrogen levels. Testosterone becomes a key endogenous source for estrogen in postmenopausal women through aromatization, which explains why both hormones should be considered in comprehensive hormone therapy.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 5,
      title: "Estrogen's Effects on Neurological Health",
      content: (
        <>
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <h3 className="font-medium text-blue-900 mb-2">Neurotrophic and Neuroprotective Actions</h3>
            <p className="mb-2">
              Estrogen serves as a key neurotrophic and neuroprotective agent in the brain, which helps explain why neurodegenerative disease risk increases after menopause. Its neurological actions are complex and multilevel:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Promotes neurogenesis and enhances dendritic spine density in hippocampal neurons</li>
              <li>Increases BDNF (brain-derived neurotrophic factor) production</li>
              <li>Enhances synaptic plasticity through modulation of NMDA and AMPA receptors</li>
              <li>Prevents atrophy of cholinergic neurons involved in memory formation</li>
              <li>Improves cerebral glucose metabolism and blood flow</li>
            </ul>
          </div>
          
          <ScientificDiagram 
            title="Estrogen's Neuroprotective Mechanisms" 
            caption="Key cellular pathways through which estrogen protects neural tissue"
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="40" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Estrogen's Neuroprotective Mechanisms</text>
                
                {/* Central neuron */}
                <g transform="translate(400, 220)">
                  {/* Cell body */}
                  <ellipse cx="0" cy="0" rx="120" ry="110" fill="#EFF8FB" stroke="#4682B4" strokeWidth="2.5" />
                  
                  {/* Nucleus */}
                  <ellipse cx="0" cy="10" rx="45" ry="40" fill="#FFFFFF" stroke="#666" strokeWidth="1.5" />
                  <text x="0" y="15" textAnchor="middle" fontSize="12" fill="#666">Nucleus</text>
                  
                  {/* Mitochondria */}
                  <g transform="translate(-50, -30)">
                    <ellipse cx="0" cy="0" rx="20" ry="12" fill="#FFECB3" stroke="#E67E22" strokeWidth="1.5" />
                    <text x="0" y="4" textAnchor="middle" fontSize="10" fill="#333">Mito</text>
                  </g>
                  
                  <g transform="translate(40, -40)">
                    <ellipse cx="0" cy="0" rx="20" ry="12" fill="#FFECB3" stroke="#E67E22" strokeWidth="1.5" />
                    <text x="0" y="4" textAnchor="middle" fontSize="10" fill="#333">Mito</text>
                  </g>
                  
                  <g transform="translate(60, 30)">
                    <ellipse cx="0" cy="0" rx="20" ry="12" fill="#FFECB3" stroke="#E67E22" strokeWidth="1.5" />
                    <text x="0" y="4" textAnchor="middle" fontSize="10" fill="#333">Mito</text>
                  </g>
                  
                  {/* Dendrites */}
                  <path d="M-100,-50 L-160,-100" stroke="#4682B4" strokeWidth="3" fill="none" />
                  <path d="M-100,-30 L-150,-30" stroke="#4682B4" strokeWidth="3" fill="none" />
                  <path d="M-90,-70 L-120,-120" stroke="#4682B4" strokeWidth="3" fill="none" />
                  
                  {/* Axon */}
                  <path d="M100,50 L170,80" stroke="#4682B4" strokeWidth="4" fill="none" />
                  
                  {/* Text labels */}
                  <text x="0" y="-90" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Neuronal Cell</text>
                </g>
                
                {/* Mechanisms around the neuron */}
                {/* 1. Antioxidant Actions */}
                <g transform="translate(200, 120)">
                  <rect x="-80" y="-25" width="160" height="50" rx="8" fill="#E8F8F5" stroke="#16A085" strokeWidth="1.5" />
                  <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#16A085">Antioxidant Effects</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#333">↑ SOD, GSH, Catalase</text>
                  
                  <path d="M0,30 L0,70 L150,130" stroke="#16A085" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray="5,3" />
                </g>
                
                {/* 2. Anti-apoptotic */}
                <g transform="translate(150, 320)">
                  <rect x="-80" y="-25" width="160" height="50" rx="8" fill="#FADBD8" stroke="#C0392B" strokeWidth="1.5" />
                  <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#C0392B">Anti-apoptotic</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#333">↑ Bcl-2, ↓ Caspase-3</text>
                  
                  <path d="M50,-10 L100,-40 L200,-70" stroke="#C0392B" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray="5,3" />
                </g>
                
                {/* 3. Neurotrophic */}
                <g transform="translate(600, 150)">
                  <rect x="-80" y="-25" width="160" height="50" rx="8" fill="#D4E6F1" stroke="#2980B9" strokeWidth="1.5" />
                  <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2980B9">Neurotrophic Support</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#333">↑ BDNF, NGF, IGF-1</text>
                  
                  <path d="M-80,0 L-110,20" stroke="#2980B9" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray="5,3" />
                </g>
                
                {/* 4. Mitochondrial */}
                <g transform="translate(630, 260)">
                  <rect x="-80" y="-25" width="160" height="50" rx="8" fill="#FCF3CF" stroke="#F39C12" strokeWidth="1.5" />
                  <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#F39C12">Mitochondrial Function</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#333">↑ ATP, ↓ ROS production</text>
                  
                  <path d="M-80,0 L-120,-25" stroke="#F39C12" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray="5,3" />
                </g>
                
                {/* 5. Anti-inflammatory */}
                <g transform="translate(450, 370)">
                  <rect x="-80" y="-25" width="160" height="50" rx="8" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#27AE60">Anti-inflammatory</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#333">↓ TNF-α, IL-1β, NF-κB</text>
                  
                  <path d="M-30,-20 L-50,-50 L-70,-90" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray="5,3" />
                </g>
                
                {/* Definitions */}
                <defs>
                  <marker id="arrowhead" markerWidth="5" markerHeight="5" refX="5" refY="2.5" orient="auto">
                    <polygon points="0 0, 5 2.5, 0 5" fill="#888" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Mitochondrial Effects</h3>
              <p>Estrogen receptors are actually present within mitochondria of neural cells, indicating direct action on cellular energy generators. Estrogen upregulates antioxidant enzymes like mitochondrial MnSOD and increases ATP production while reducing reactive oxygen species (ROS). This mitochondrial protection represents a key mechanism for estrogen's neuroprotective effects.</p>
            </div>
            <div className="border border-gray-200 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Clinical Implications</h3>
              <p>Clinically, women report improved mood, memory, verbal fluency, and sleep with adequate estrogen therapy. Estrogen deprivation leads to cognitive fog, increased anxiety/depression, and potentially a higher long-term risk of neurodegenerative diseases. Evidence suggests the earlier estrogen therapy begins post-menopause (within the "critical window"), the greater the neuroprotective benefits.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Estrogen's neuroprotective effects occur through multiple mechanisms including antioxidant actions, anti-apoptotic signaling, neurotrophic support, mitochondrial protection, and anti-inflammatory effects. This comprehensive neuroprotection helps explain longevity differences between sexes and provides opportunity for preventing cognitive decline. For optimal brain health, hormone therapy initiated during the "critical window" (ideally within 5-10 years of menopause) provides the greatest benefit while minimizing risks.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "Estrogen's Cardiovascular and Bone Effects",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <ScientificDiagram 
                title="Cardiovascular System Effects" 
                caption="Key mechanisms of estrogen's vascular protection"
              >
                <div className="p-4">
                  <svg viewBox="0 0 350 350" className="w-full">
                    {/* Blood vessel cross-section */}
                    <circle cx="175" cy="175" r="150" fill="#F5F5F5" stroke="#666" strokeWidth="2" />
                    <circle cx="175" cy="175" r="100" fill="#FADBD8" stroke="#E74C3C" strokeWidth="2" />
                    
                    {/* Endothelial cells */}
                    <path d="M275,175 A100,100 0 0,1 175,275" fill="none" stroke="#E74C3C" strokeWidth="8" />
                    <path d="M175,75 A100,100 0 0,1 275,175" fill="none" stroke="#E74C3C" strokeWidth="8" />
                    <path d="M75,175 A100,100 0 0,1 175,75" fill="none" stroke="#E74C3C" strokeWidth="8" />
                    <path d="M175,275 A100,100 0 0,1 75,175" fill="none" stroke="#E74C3C" strokeWidth="8" />
                    
                    {/* Text Labels */}
                    <text x="175" y="40" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Blood Vessel</text>
                    <text x="175" y="175" textAnchor="middle" fontSize="14" fill="#333">Lumen</text>
                    <text x="175" y="313" textAnchor="middle" fontSize="12" fill="#666">Smooth Muscle</text>
                    <text x="175" y="330" textAnchor="middle" fontSize="12" fill="#666">Layer</text>
                    
                    {/* Estrogen Effects */}
                    <g transform="translate(60, 110)">
                      <circle cx="0" cy="0" r="25" fill="#D4E6F1" stroke="#3498DB" strokeWidth="1.5" />
                      <text x="0" y="-5" textAnchor="middle" fontSize="11" fill="#333">Vasodilation</text>
                      <text x="0" y="10" textAnchor="middle" fontSize="10" fill="#333">↑ eNOS</text>
                    </g>
                    
                    <g transform="translate(290, 110)">
                      <circle cx="0" cy="0" r="25" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                      <text x="0" y="-5" textAnchor="middle" fontSize="11" fill="#333">Anti-</text>
                      <text x="0" y="8" textAnchor="middle" fontSize="11" fill="#333">Inflammatory</text>
                    </g>
                    
                    <g transform="translate(290, 240)">
                      <circle cx="0" cy="0" r="25" fill="#FCF3CF" stroke="#F39C12" strokeWidth="1.5" />
                      <text x="0" y="-5" textAnchor="middle" fontSize="11" fill="#333">Anti-</text>
                      <text x="0" y="8" textAnchor="middle" fontSize="11" fill="#333">Atherogenic</text>
                    </g>
                    
                    <g transform="translate(60, 240)">
                      <circle cx="0" cy="0" r="25" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                      <text x="0" y="-5" textAnchor="middle" fontSize="11" fill="#333">Improves</text>
                      <text x="0" y="8" textAnchor="middle" fontSize="11" fill="#333">Lipid Profile</text>
                    </g>
                  </svg>
                </div>
              </ScientificDiagram>
              
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li><span className="font-medium">Vasodilation:</span> Increases nitric oxide (NO) production through rapid non-genomic pathway</li>
                <li><span className="font-medium">Anti-inflammatory:</span> Reduces expression of adhesion molecules and pro-inflammatory cytokines</li>
                <li><span className="font-medium">Lipids:</span> Increases HDL, decreases LDL and triglycerides</li>
                <li><span className="font-medium">Glucose metabolism:</span> Improves insulin sensitivity</li>
              </ul>
            </div>
            
            <div>
              <ScientificDiagram 
                title="Bone Health Effects" 
                caption="Estrogen's regulation of bone remodeling"
              >
                <div className="p-4">
                  <svg viewBox="0 0 350 350" className="w-full">
                    {/* Bone cross-section */}
                    <rect x="75" y="125" width="200" height="100" rx="10" fill="#F5F5F5" stroke="#666" strokeWidth="2" />
                    
                    {/* Bone matrix */}
                    <pattern id="bonePattern" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">
                      <rect width="5" height="5" fill="#E6E6E6" />
                    </pattern>
                    <rect x="80" y="130" width="190" height="90" rx="8" fill="url(#bonePattern)" />
                    
                    {/* Bone cells */}
                    <g transform="translate(125, 200)">
                      <circle cx="0" cy="0" r="15" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                      <text x="0" y="4" textAnchor="middle" fontSize="10" fill="#333">Osteoclast</text>
                      <text x="0" y="25" textAnchor="middle" fontSize="9" fill="#666">Bone Resorption</text>
                    </g>
                    
                    <g transform="translate(225, 200)">
                      <circle cx="0" cy="0" r="15" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                      <text x="0" y="4" textAnchor="middle" fontSize="10" fill="#333">Osteoblast</text>
                      <text x="0" y="25" textAnchor="middle" fontSize="9" fill="#666">Bone Formation</text>
                    </g>
                    
                    {/* Estrogen Effects */}
                    <g transform="translate(125, 150)">
                      <path d="M0,0 L0,-25" stroke="#E74C3C" strokeWidth="2" markerEnd="url(#redarrow)" />
                      <text x="0" y="-35" textAnchor="middle" fontSize="12" fill="#E74C3C">Inhibits</text>
                    </g>
                    
                    <g transform="translate(225, 150)">
                      <path d="M0,0 L0,-25" stroke="#27AE60" strokeWidth="2" markerEnd="url(#greenarrow)" />
                      <text x="0" y="-35" textAnchor="middle" fontSize="12" fill="#27AE60">Supports</text>
                    </g>
                    
                    <text x="175" y="95" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Estrogen Effect on Bone Remodeling</text>
                    
                    {/* Balance scale effect */}
                    <line x1="145" y1="255" x2="205" y2="255" stroke="#666" strokeWidth="2" />
                    <text x="125" y="275" textAnchor="middle" fontSize="11" fill="#E74C3C">↑ in Menopause</text>
                    <text x="225" y="275" textAnchor="middle" fontSize="11" fill="#27AE60">↓ in Menopause</text>
                    
                    <path d="M175,285 L175,300 L120,330" stroke="#666" strokeWidth="1.5" strokeDasharray="3,2" />
                    <path d="M175,285 L175,300 L230,330" stroke="#666" strokeWidth="1.5" strokeDasharray="3,2" />
                    <text x="115" y="345" textAnchor="middle" fontSize="12" fill="#333">Bone Loss</text>
                    <text x="235" y="345" textAnchor="middle" fontSize="12" fill="#333">Fracture Risk</text>
                    
                    {/* Custom arrowheads */}
                    <defs>
                      <marker id="redarrow" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                        <path d="M0,0 L10,5 L0,10 Z" fill="#E74C3C" />
                      </marker>
                      <marker id="greenarrow" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                        <path d="M0,0 L10,5 L0,10 Z" fill="#27AE60" />
                      </marker>
                    </defs>
                  </svg>
                </div>
              </ScientificDiagram>
              
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li><span className="font-medium">Bone homeostasis:</span> Maintains balance between bone formation and resorption</li>
                <li><span className="font-medium">Osteoclast inhibition:</span> Primary effect is restraining bone breakdown</li>
                <li><span className="font-medium">Menopausal bone loss:</span> 2-5% bone mass loss per year in first 5 years</li>
                <li><span className="font-medium">Fracture risk:</span> Most significant for vertebral and hip fractures</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-amber-50 p-4 rounded-md mb-6">
            <h3 className="font-medium text-amber-900 mb-2">The "Timing Hypothesis" for Estrogen Therapy</h3>
            <p>
              The timing of estrogen therapy initiation is crucial for both cardiovascular and bone health. Multiple lines of evidence support that estrogen's benefits are highest and risks lowest when started during the "critical window" (typically within 10 years of menopause onset).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <h4 className="font-medium text-amber-800 text-sm mb-1">Early Initiation ({"<"}10 years post-menopause):</h4>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Significant cardioprotective effects</li>
                  <li>Maximum bone preservation</li>
                  <li>Optimal neuroprotection</li>
                  <li>Lower VTE and stroke risk</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-amber-800 text-sm mb-1">Late Initiation ({">"} 10 years post-menopause):</h4>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Reduced or absent cardioprotection</li>
                  <li>Less effective bone preservation</li>
                  <li>Potentially increased stroke risk</li>
                  <li>May still provide symptom relief</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Estrogen's effects on cardiovascular and bone health demonstrate its role as a system-wide regulator in the body. In the cardiovascular system, it promotes endothelial health through nitric oxide production, reduces inflammation, and improves lipid profiles. For bone health, estrogen maintains homeostasis primarily by restraining osteoclast activity. The "timing hypothesis" is crucial - initiating therapy early in menopause maximizes cardioprotective and bone-preserving benefits while minimizing risks. This timing consideration is a key aspect of personalized hormone therapy.
          </KeyTakeaway>
        </>
      )
    }
  ];

    return (
    <MentorshipAccessGate>
      <Navbar />
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Main content */}
          <div className="md:col-span-3">
            <h1 className="text-3xl font-bold mb-4">Estrogen: Molecular Foundations</h1>
            <p className="text-gray-600 mb-8">
              A systems-based molecular exploration of estrogen metabolism, receptor dynamics, and clinical implications.
            </p>
            
            {/* Slideshow */}
            <Slideshow slides={lectureSlides} />
            
            {/* Video placeholder */}
            <VideoPlaceholder />
          </div>
          
          {/* Sidebar */}
          <div className="md:col-span-1">
            <Card className="p-4 mb-6">
              <h3 className="font-medium text-lg mb-4">Resources</h3>
              <ul className="space-y-2">
                {resources.map((resource, index) => (
                  <li key={index}>
                    <a 
                      href={resource.link} 
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <span className="mr-2">{resource.icon}</span>
                      {resource.title}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </MentorshipAccessGate>
  );
};

export default EstrogenMolecularFoundations;